import { useState } from 'react'
import { MailCheck } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { betterAuthClient } from '@/lib/auth/better-auth-client'

/**
 * Asks a customer whose address is not yet confirmed to confirm it. Until
 * they do, the consignments, requests and parcels Xtellar holds under their
 * email stay hidden: anyone can open an account with any address. The shell
 * shows it to customer logins only.
 */
export function EmailConfirmationBanner() {
  const { data } = betterAuthClient.useSession()
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle')
  const user = data?.user
  if (!user || user.emailVerified) return null

  const send = async () => {
    setState('sending')
    try {
      const result = await betterAuthClient.sendVerificationEmail({
        email: user.email,
        callbackURL: `${window.location.origin}/parcels`,
      })
      setState(result.error ? 'failed' : 'sent')
    } catch {
      setState('failed')
    }
  }

  return (
    <Alert className="mb-4 md:mb-6">
      <MailCheck className="h-4 w-4" />
      <AlertTitle>Confirm your email address</AlertTitle>
      <AlertDescription>
        <p>
          Your consignments, requests and parcels show here once you confirm that {user.email} is
          yours.
          {state === 'sent' && ' We sent you a link; it works for 24 hours.'}
          {state === 'failed' && ' The link could not be sent. Please try again.'}
        </p>
        {state !== 'sent' && (
          <Button size="sm" className="mt-3" onClick={send} disabled={state === 'sending'}>
            {state === 'sending' ? 'Sending…' : 'Send me the link'}
          </Button>
        )}
      </AlertDescription>
    </Alert>
  )
}
